import { Box, IconButton, Input, InputAdornment, InputLabel, SxProps } from "@mui/material";
import React, { Component } from "react";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import AttachIcon from "../resources/images/icons-attachment.svg";
import RemoveIcon from "../resources/images/icons-remove.svg";
import { TextField } from "./TextField";
import { Tooltip } from "./Tooltip";

interface FileUploadProps extends WithLocaleProps {
  id?: string;
  className?: string | null;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  file?: File;
  onRemoveFile?: (e: React.MouseEvent, file: File) => void;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  maxFileSize?: number;
  errorMimeType?: string;
  errorFileSize?: string;
  onFileChange?: (e: any, file: File, multiple: boolean) => void;
  onError?: (error: string | null) => void;
  onMultipleFilesSelected?: (e: any, files: File[]) => void;
  sx?: SxProps;
  helperText?: string;
  multiple?: boolean;
  accept?: string;
  hasRemove?: boolean;
}

interface FileUploadState {
  file?: File;
  error?: string | null;
}

class FileUploadBase extends Component<FileUploadProps, FileUploadState> {
  static defaultProps = {
    allowedMimeTypes: ["*/*"],
    allowedExtensions: [],
    maxFileSize: 10240000,
    hasRemove: false,
    multiple: false
  };

  inputRef: React.RefObject<HTMLInputElement | null>;

  constructor(props: FileUploadProps) {
    super(props);

    this.state = {
      file: this.props.file,
      error: null
    };

    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps: FileUploadProps, prevState: FileUploadState) {
    if (prevProps.file !== this.props.file) this.setState({ file: this.props.file });
  }

  onChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.filesChange(e, e.target.files);
  }

  filesChange(e: React.ChangeEvent<HTMLInputElement>, files: FileList | null) {
    const { onMultipleFilesSelected } = this.props;

    if (!files) return;

    this.fileChange(e, files[0], files.length > 1);
    if (files.length > 1 && onMultipleFilesSelected)
      onMultipleFilesSelected(e, [...files].slice(1));
  }

  fileChange(e: React.ChangeEvent<HTMLInputElement>, file: File, multiple: boolean) {
    const {
      allowedMimeTypes,
      allowedExtensions,
      maxFileSize,
      errorMimeType,
      errorFileSize,
      onFileChange,
      onError,
      translate
    } = this.props;

    const defaultErrorMsg = translate("messages.pleaseUploadValidFile");
    const mimeTypeError = errorMimeType || defaultErrorMsg;
    const fileSizeError = errorFileSize || defaultErrorMsg;

    if (!allowedMimeTypes?.includes(file.type)) {
      this.setState({ error: mimeTypeError });
      if (onError) onError(mimeTypeError);
      return;
    }

    const fileExtension = file.name?.split(".").pop()?.toLowerCase();
    if (
      allowedExtensions?.length &&
      (!fileExtension || !allowedExtensions.includes(fileExtension))
    ) {
      this.setState({ error: mimeTypeError });
      if (onError) onError(mimeTypeError);
      return;
    }

    if (file.size > (maxFileSize || FileUploadBase.defaultProps.maxFileSize)) {
      this.setState({ error: fileSizeError });
      if (onError) onError(fileSizeError);
      return;
    }

    this.setState({ file: file, error: null });
    if (onError) onError(null);

    if (onFileChange) onFileChange(e, file, multiple);
  }

  selectFile(e: any) {
    this.inputRef.current?.click();
  }

  dropFile(e: any) {
    this.filesChange(e, e.dataTransfer.files);
  }

  render() {
    const {
      id,
      className,
      disabled,
      label,
      placeholder,
      onRemoveFile,
      allowedMimeTypes,
      sx,
      helperText,
      multiple,
      translate
    } = this.props;
    const { file, error } = this.state;

    const chooseFileLabel = translate("tooltips.chooseFile");
    const removeLabel = translate("common.remove");

    return (
      <Box className={"klayo-singlefileupload" + (className ? +" " + className : "")}>
        <Input
          id={id + "_file"}
          inputProps={{
            ref: this.inputRef,
            accept: allowedMimeTypes?.join(", "),
            multiple
          }}
          onChange={this.onChange.bind(this)}
          type='file'
          value=''
          style={{ display: "none" }}
        />

        <TextField
          id={id}
          placeholder={placeholder}
          label={label}
          disabled={disabled}
          autoComplete={false}
          sx={sx}
          helperText={error || helperText}
          error={error !== null}
          value={file ? file.name : ""}
          endAdornment={
            <InputAdornment position='end'>
              <InputLabel htmlFor={id + "_file"}>
                <Tooltip title={chooseFileLabel}>
                  <IconButton aria-label={chooseFileLabel} component='span'>
                    <AttachIcon />
                  </IconButton>
                </Tooltip>
              </InputLabel>
              {onRemoveFile && file && (
                <Tooltip title={removeLabel}>
                  <IconButton
                    aria-label={removeLabel}
                    component='span'
                    onClick={(e) => onRemoveFile(e, file)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          }
        />
      </Box>
    );
  }
}

export const FileUpload = withLocale(FileUploadBase);
